import { Stack } from "@mui/material";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import type {
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  ReportField,
} from "../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../api/types/svTypes.ts";
import { selectTabularFields } from "../../../store/selectors/fieldsStateSelectors.ts";
import { isDimension, isIrr } from "../../../utilities/fieldsHelper.ts";
import DropFieldContainer from "../common/fields/DropFieldContainer.tsx";
import FieldOptionPopupContainer from "../common/fields/measures/FieldOptionPopupContainer.tsx";
import MeasureFieldOptions from "../common/fields/measures/MeasureFieldOptions.tsx";
import useFieldsDropContainer from "../common/table/hooks/useFieldsDropContainer.tsx";
import { getKeyValue } from "../common/table/tableHelper.ts";
import { canDropCashFlowField, createCashFlowField } from "./cashFlowMeasureDrop.ts";

import CustomLabel from "../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../viewer/common/fields/FieldOptionsDivider.tsx";

const acceptedDropTypes = [AreaItemType.MEASURE, AreaItemType.DIMENSION];

interface Props {
  readonly: boolean;
}

const CashFlowMeasureDropContainer = ({ readonly }: Props) => {
  const fields = useSelector(selectTabularFields);
  const {
    createItem,
    handleRemove,
    closePopup,
    handleUpdateConfiguration,
    handleDimensionFieldCustomLabelChange,
    moveItem,
    addItem,
    anchorEl,
    currentField,
    removeItem,
    dimensionFields,
    conditions,
  } = useFieldsDropContainer(fields, { readonlyMode: readonly });
  const handleCanDropItem = useCallback(
    (item: FieldWrapper<DraggableFieldType> | DraggableMetaType) => canDropCashFlowField(item, fields, readonly),
    [fields, readonly]
  );

  return (
    <Stack spacing={1}>
      <DropFieldContainer<ReportField, DraggableMetaType>
        title="Fields"
        areaFieldType={AreaItemType.FIELDS}
        acceptedDropTypes={acceptedDropTypes}
        fields={fields}
        canDropItem={handleCanDropItem}
        renderListItem={createItem}
        getKeyValue={getKeyValue}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        createListItem={createCashFlowField}
        readonly={readonly}
      />
      <FieldOptionPopupContainer anchorEl={anchorEl} cancel={closePopup}>
        {isDimension(currentField) && (
          <>
            <CustomLabel
              customFieldValue={currentField.config.customLabel}
              originalFieldValue={currentField.meta.caption}
              updateCustomFieldValue={handleDimensionFieldCustomLabelChange}
              readonly={readonly}
            />
            {!readonly && (
              <>
                <FieldOptionsDivider />
                <FieldOptionDeleteButton onRemove={handleRemove} />
              </>
            )}
          </>
        )}
        {isIrr(currentField) && (
          <MeasureFieldOptions
            field={currentField}
            dimensionFields={dimensionFields}
            conditions={conditions}
            otherOptions={<></>}
            cancel={closePopup}
            saveChanges={handleUpdateConfiguration}
            onRemove={handleRemove}
            canAddCustomField={false}
            onCalculationItemClick={undefined}
            readonly={readonly}
          />
        )}
      </FieldOptionPopupContainer>
    </Stack>
  );
};

export default CashFlowMeasureDropContainer;
